import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@diggens/api-client';

interface SlackStatus {
  linked: boolean;
}

interface LinkCode {
  code: string;
  expiresAt: string;
}

const SLACK_QUERY_KEY = ['integrations', 'slack'] as const;

/**
 * Settings > Connections card that links the user's Slack account, so the Diggens bot knows who they are in Slack. A
 * matching Slack email links on the first message; otherwise the user takes a one-time code from here to the bot. The
 * status refetches when the user comes back to this tab after sending the code.
 */
export function SlackConnection() {
  const status = useQuery({
    queryKey: SLACK_QUERY_KEY,
    queryFn: () => apiFetch<SlackStatus>('/integrations/slack'),
    // The app default (no refetch on focus, five minutes fresh) would keep showing "not linked" after the code is sent.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const code = useMutation({
    mutationFn: () => apiFetch<LinkCode>('/integrations/slack/link-code', { method: 'POST' }),
  });

  if (status.isPending) {
    return (
      <Box>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const linked = status.data?.linked === true;
  const expiresAt = code.data ? new Date(code.data.expiresAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null;

  return (
    <Card sx={{ maxWidth: 800 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Slack
        </Typography>

        {status.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load your Slack link.
          </Alert>
        )}
        {code.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not get a link code. Try again.
          </Alert>
        )}

        {linked ? (
          <Typography>Your Slack account is linked. Message the Diggens bot in Slack to talk to your Chief of Staff.</Typography>
        ) : (
          status.data && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography color="text.secondary">
                Message the Diggens bot in Slack. If your Slack email matches your Diggens email, it links you on your first message. Otherwise, get a code here and send it to the bot.
              </Typography>
              <Button variant="contained" onClick={() => code.mutate()} disabled={code.isPending} sx={{ flexShrink: 0 }}>
                Get a link code
              </Button>
            </Box>
          )
        )}
        {!linked && code.data && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            Send{' '}
            <Box component="code" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {`link ${code.data.code}`}
            </Box>{' '}
            to the Diggens bot in a direct message before {expiresAt}. The code works once.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
